import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import {
  Sparkles,
  School,
  GraduationCap,
  Lightbulb,
  FileQuestion,
  BookOpen,
  LogIn,
  ChevronDown,
  Menu,
  X,
  ShoppingCart,
} from "lucide-react";

export default function SharedHeader({ 
  rightContent = null,
  className = "",
  transparent = false,
  showTagline = false,
  showNav = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount, setIsCartOpen } = useCart();
  const cartCount = getCartCount();
  const isLandingPage = location.pathname === "/";
  const [eduOneOpen, setEduOneOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setEduOneOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogoClick = () => {
    if (!isLandingPage) {
      navigate("/");
    }
  };

  return (
    <header className={`w-full py-2 px-4 sm:px-6 lg:px-8 ${transparent ? '' : 'bg-white/95 backdrop-blur-md border-b border-gray-100'} ${className} sticky top-0 z-50`}>
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Logo Section */}
        <div className="flex items-center gap-1">
          <div className="flex flex-col">
            <img 
              src="/eduzy-logo-transparent.png" 
              alt="Eduzy - AI-Powered Learning Platform" 
              className={`h-24 w-auto ${!isLandingPage ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
              style={{ height: '5rem' }}
              onClick={handleLogoClick}
              data-testid="header-logo"
            />
            {showTagline && (
              <span className="text-xs text-emerald-600 font-medium tracking-wide -mt-2 ml-1">
                Learn Smart. Learn Easy.
              </span>
            )}
          </div>
          <a 
            href="https://contentiumtech.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:opacity-100 opacity-80 transition-opacity hidden sm:block"
          >
            <img 
              src="/contentium-tech-logo.svg" 
              alt="Contentium Tech" 
              className="h-auto"
              style={{ height: '4rem' }}
              data-testid="header-contentium-logo"
            />
          </a>
        </div>

        {/* Desktop Navigation */}
        {showNav && (
          <div className="hidden lg:flex items-center gap-2">
            {/* EduOne AI Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                onMouseEnter={() => setEduOneOpen(true)}
                onClick={() => setEduOneOpen(!eduOneOpen)}
                variant="ghost"
                className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
                data-testid="nav-eduone-btn"
                title="For Individual Students & Parents"
              >
                <Sparkles className="w-4 h-4" />
                EduOne AI
                <ChevronDown className={`w-4 h-4 transition-transform ${eduOneOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {/* EduOne AI Dropdown Menu */}
              {eduOneOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9999]"
                  onMouseLeave={() => setEduOneOpen(false)}
                >
                  <button
                    onClick={() => { navigate("/eduwise"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                    data-testid="dropdown-eduwise"
                  >
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduWise</div>
                      <div className="text-xs text-gray-500">Personalized AI Tutor</div>
                    </div>
                  </button>
                  <button
                    onClick={() => { navigate("/eduassist"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50 transition-colors"
                    data-testid="dropdown-eduassist"
                  >
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduAssist</div>
                      <div className="text-xs text-gray-500">Personal AI Homework Assistant</div>
                    </div>
                  </button>
                  <button
                    onClick={() => { navigate("/eduquest"); setEduOneOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors"
                    data-testid="dropdown-eduquest"
                  >
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileQuestion className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">EduQuest</div>
                      <div className="text-xs text-gray-500">Book-Grounded Question Banks</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* EduSphere AI Button */}
            <Button
              onClick={() => navigate("/edusphere")}
              variant="ghost"
              className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold flex items-center gap-2 px-4 py-2 h-10 rounded-full"
              data-testid="nav-edusphere-btn"
              title="For Schools & Institutions"
            >
              <div className="relative w-4 h-4">
                <div className="absolute inset-0 border-2 border-indigo-400 rounded-full"></div>
                <School className="w-2.5 h-2.5 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              EduSphere AI
            </Button>

            {/* Blog Button */}
            <Button
              onClick={() => navigate("/blog")}
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10"
              data-testid="nav-blog-btn"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </Button>

            {/* Cart Button */}
            <Button
              onClick={() => setIsCartOpen(true)}
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100 font-medium flex items-center gap-2 px-3 py-2 h-10 relative"
              data-testid="nav-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Button>

            <div className="w-px h-6 bg-gray-200 mx-1"></div>
            
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-4 py-2 h-10"
              data-testid="nav-login-btn"
            >
              <LogIn className="w-4 h-4 mr-1.5" />
              Login
            </Button>
          </div>
        )}

        {/* Mobile Menu Button */}
        {showNav && (
          <div className="lg:hidden flex items-center gap-2">
            <Button
              onClick={() => setIsCartOpen(true)}
              size="sm"
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100 px-2 relative"
              data-testid="nav-cart-btn-mobile"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Button>
            <Button
              onClick={() => navigate("/login")}
              size="sm"
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-3 py-2 h-auto"
              data-testid="nav-login-btn-mobile"
            >
              <LogIn className="w-4 h-4 mr-1" />
              Login
            </Button>
            <Button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              size="sm"
              variant="ghost"
              className="text-gray-700 px-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        )}

        {/* Custom Right Content (overrides nav) */}
        {rightContent && !showNav && (
          <div className="flex items-center gap-3">
            {rightContent}
          </div>
        )}
      </div>

      {/* Mobile Menu Dropdown */}
      {showNav && mobileMenuOpen && (
        <div className="lg:hidden absolute left-0 right-0 top-full bg-white border-b border-gray-200 shadow-lg animate-in slide-in-from-top-2 duration-200 max-h-[70vh] overflow-y-auto z-[9999]">
          <div className="px-4 py-3 space-y-1">
            {/* EduOne AI Section */}
            <div className="bg-emerald-50 rounded-lg p-3 mb-2">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <span className="font-bold text-emerald-800">EduOne AI</span>
              </div>
              <div className="grid grid-cols-1 gap-1 ml-2">
                <button
                  onClick={() => { navigate("/eduwise"); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                  data-testid="mobile-nav-eduwise"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">EduWise</div>
                    <div className="text-xs text-gray-500">Personalized AI Tutor</div>
                  </div>
                </button>
                <button
                  onClick={() => { navigate("/eduassist"); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                  data-testid="mobile-nav-eduassist"
                >
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">EduAssist</div>
                    <div className="text-xs text-gray-500">Personal AI Homework Assistant</div>
                  </div>
                </button>
                <button
                  onClick={() => { navigate("/eduquest"); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-white rounded-lg transition-colors"
                  data-testid="mobile-nav-eduquest"
                >
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileQuestion className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">EduQuest</div>
                    <div className="text-xs text-gray-500">AI Generated Question Banks</div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* EduSphere AI */}
            <button
              onClick={() => { navigate("/edusphere"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              data-testid="mobile-nav-edusphere"
            >
              <div className="relative w-5 h-5">
                <div className="absolute inset-0 border-2 border-indigo-400 rounded-full"></div>
                <School className="w-3 h-3 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <span className="font-semibold text-indigo-700">EduSphere AI</span>
            </button>
            
            {/* Blog */}
            <button
              onClick={() => { navigate("/blog"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              data-testid="mobile-nav-blog"
            >
              <BookOpen className="w-5 h-5" />
              <span className="font-medium">Blog</span>
            </button>
            
            {/* EduQuest Shop */}
            <button
              onClick={() => { navigate("/eduquest"); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              data-testid="mobile-nav-shop"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="font-medium">EduQuest Shop</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
